/**
 * Audio waveform overlay — visual feedback for audio-only uploads.
 *
 * Why this file exists:
 *   The auto-captions stage uses TimbricaPlayer which mounts a <video>
 *   element. When the user uploads an MP3 / WAV / M4A (no video stream),
 *   the <video> element renders a black canvas — visually broken UX. This
 *   module replaces that black void with a pro-grade waveform on a
 *   <canvas> overlay, plus an animated EQ-style frequency bar bloom
 *   during playback, all client-side via the Web Audio API.
 *
 * Two render modes:
 *   – Static waveform: drawn once after decode from the AudioBuffer's
 *     PCM samples. Visible while paused.
 *   – Live frequency bars: AnalyserNode → bins → RAF loop while playing.
 *     Layered ON TOP of the static waveform for depth.
 *
 * Public API (window.__acWaveform):
 *   bind(stageEl, videoEl, file, opts)  — call after TimbricaPlayer mounts;
 *                                          decides whether to activate based on
 *                                          file.type. Returns { destroy() }.
 *   unbind()                             — tear down on resetWorkspace.
 */
(function(){
    'use strict';

    var WAVEFORM_BG = '#0b1020';
    var WAVEFORM_FG = 'rgba(56,189,248,0.80)';      // sky-400 @ 80%
    var WAVEFORM_FG_DIM = 'rgba(56,189,248,0.30)';
    var BARS_FG = 'rgba(255,235,59,0.85)';           // matches emphasis yellow
    var BAR_COUNT = 64;

    var state = null;

    function destroyState() {
        if (!state) return;
        try { if (state.raf) cancelAnimationFrame(state.raf); } catch(_){}
        try { if (state.audioCtx && state.audioCtx.state !== 'closed') state.audioCtx.close(); } catch(_){}
        if (state.canvasEl && state.canvasEl.parentNode) state.canvasEl.parentNode.removeChild(state.canvasEl);
        // Detach listeners we attached
        if (state.video && state.handlers) {
            state.video.removeEventListener('play',    state.handlers.onPlay);
            state.video.removeEventListener('pause',   state.handlers.onPause);
            state.video.removeEventListener('seeked',  state.handlers.onSeeked);
            state.video.removeEventListener('ended',   state.handlers.onPause);
        }
        state = null;
    }

    /* Decode PCM via OfflineAudioContext for the static waveform render.
       We don't use the live AudioContext for decode — that one is wired to
       the <video> for the playback analyser, decoding twice on it is
       wasteful and on iOS can fail with NoModificationAllowedError. */
    async function decodeForStatic(file) {
        var ab = await file.arrayBuffer();
        var ctx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(1, 44100, 44100);
        return await new Promise(function(resolve, reject){
            try {
                ctx.decodeAudioData(ab.slice(0), resolve, reject);
            } catch(e){ reject(e); }
        });
    }

    /* Down-sample a Float32 channel to N peaks (max abs in each bucket). */
    function buildPeaks(samples, n) {
        var peaks = new Float32Array(n);
        var step = Math.max(1, Math.floor(samples.length / n));
        for (var i = 0; i < n; i++) {
            var lo = i * step;
            var hi = Math.min(samples.length, lo + step);
            var p = 0;
            for (var j = lo; j < hi; j++) {
                var v = Math.abs(samples[j]);
                if (v > p) p = v;
            }
            peaks[i] = p;
        }
        return peaks;
    }

    /* Static waveform: filled lower mirror against a 0-line, rendering peaks
       across the full canvas width. Drawn once per decode + once per resize. */
    function drawStatic(ctx, peaks, w, h, progress01) {
        ctx.fillStyle = WAVEFORM_BG;
        ctx.fillRect(0, 0, w, h);
        var mid = h * 0.5;
        var n = peaks.length;
        var barW = Math.max(1, Math.floor(w / n) - 1);
        var gap = Math.max(0, Math.floor(w / n) - barW);
        var progressX = (progress01 || 0) * w;
        for (var i = 0; i < n; i++) {
            var x = i * (barW + gap);
            var peak = peaks[i];
            var barH = Math.max(1, peak * h * 0.85);
            ctx.fillStyle = (x + barW <= progressX) ? WAVEFORM_FG : WAVEFORM_FG_DIM;
            ctx.fillRect(x, mid - barH * 0.5, barW, barH);
        }
    }

    /* Live frequency bars: 32 bands across full width, semitransparent
       yellow, additive on top of the static waveform. Decay applied per
       frame for a smoother trail. */
    function drawLive(ctx, freqArr, w, h, decay) {
        var n = freqArr.length;
        var bins = Math.min(BAR_COUNT, n);
        var step = Math.floor(n / bins);
        var barW = Math.max(2, Math.floor(w / bins) - 2);
        var gap = Math.max(1, Math.floor(w / bins) - barW);
        ctx.globalCompositeOperation = 'lighter';
        for (var i = 0; i < bins; i++) {
            var lo = i * step;
            var hi = Math.min(n, lo + step);
            var avg = 0;
            for (var j = lo; j < hi; j++) avg += freqArr[j];
            avg /= Math.max(1, hi - lo);
            var amp = avg / 255; // 0..1
            // Keep the prior decay value if this frame is smaller — gives a
            // softer release tail.
            decay[i] = Math.max(amp, decay[i] * 0.85);
            var barH = decay[i] * h * 0.6;
            var x = i * (barW + gap);
            ctx.fillStyle = BARS_FG;
            ctx.fillRect(x, h - barH, barW, barH);
        }
        ctx.globalCompositeOperation = 'source-over';
    }

    /* Connect the playing <video> element through the live AudioContext to
       an AnalyserNode. Some browsers throw if the <video> is already
       playing when MediaElementSource is created — caller must call
       this before play(). We use bind-time as that moment. */
    function buildAnalyser(video) {
        var AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        try {
            var ctx = new AC();
            var src = ctx.createMediaElementSource(video);
            var an = ctx.createAnalyser();
            an.fftSize = 512;            // 256 frequency bins
            an.smoothingTimeConstant = 0.78;
            src.connect(an);
            an.connect(ctx.destination); // keep audio audible
            return { ctx: ctx, src: src, analyser: an, freqArr: new Uint8Array(an.frequencyBinCount) };
        } catch(e) {
            console.warn('[waveform] AnalyserNode setup failed:', e.message);
            return null;
        }
    }

    function isAudioOnly(file) {
        if (!file) return false;
        var t = (file.type || '').toLowerCase();
        if (t.indexOf('audio/') === 0) return true;
        var name = (file.name || '').toLowerCase();
        return /\.(mp3|wav|flac|m4a|ogg|opus|aac|wma)$/.test(name);
    }

    window.__acWaveform = {
        /* Decide on activation, build canvas + analyser, attach listeners. */
        bind: async function(stageEl, video, file, opts) {
            destroyState();
            if (!stageEl || !video || !file) return null;
            if (!isAudioOnly(file)) return null; // video already shows visuals

            var canvas = document.createElement('canvas');
            canvas.className = 'ac-waveform-canvas';
            canvas.style.position = 'absolute';
            canvas.style.inset = '0';
            canvas.style.width = '100%';
            canvas.style.height = '100%';
            canvas.style.zIndex = '1'; // below caption overlay (z=2)
            canvas.style.background = WAVEFORM_BG;
            canvas.style.pointerEvents = 'none';
            stageEl.appendChild(canvas);

            var dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
            function sizeCanvas() {
                var r = stageEl.getBoundingClientRect();
                canvas.width  = Math.max(320, Math.floor(r.width * dpr));
                canvas.height = Math.max(180, Math.floor(r.height * dpr));
            }
            sizeCanvas();
            window.addEventListener('resize', sizeCanvas);

            var ctx2d = canvas.getContext('2d');
            var peaks = null;
            try {
                var ab = await decodeForStatic(file);
                peaks = buildPeaks(ab.getChannelData(0), 256);
            } catch(e) {
                // Decode failure (corrupt / unsupported) — leave static empty;
                // live bars still work via AnalyserNode below.
                console.warn('[waveform] decode for static failed:', e.message);
                peaks = new Float32Array(256); // flat baseline
            }

            // Initial paint
            drawStatic(ctx2d, peaks, canvas.width, canvas.height, 0);

            var analyser = buildAnalyser(video);
            var decay = new Float32Array(BAR_COUNT);
            var raf = 0;

            function tick() {
                if (!state) return;
                var prog = video.duration > 0 ? (video.currentTime / video.duration) : 0;
                drawStatic(ctx2d, peaks, canvas.width, canvas.height, prog);
                if (analyser) {
                    analyser.analyser.getByteFrequencyData(analyser.freqArr);
                    drawLive(ctx2d, analyser.freqArr, canvas.width, canvas.height, decay);
                }
                state.raf = requestAnimationFrame(tick);
            }

            var handlers = {
                onPlay: function() {
                    if (analyser && analyser.ctx.state === 'suspended') {
                        try { analyser.ctx.resume(); } catch(_){}
                    }
                    if (!state.raf) state.raf = requestAnimationFrame(tick);
                },
                onPause: function() {
                    if (state.raf) { cancelAnimationFrame(state.raf); state.raf = 0; }
                    // Final paint with current progress so progress bar lands at the right spot
                    var prog = video.duration > 0 ? (video.currentTime / video.duration) : 0;
                    drawStatic(ctx2d, peaks, canvas.width, canvas.height, prog);
                },
                onSeeked: function() {
                    var prog = video.duration > 0 ? (video.currentTime / video.duration) : 0;
                    drawStatic(ctx2d, peaks, canvas.width, canvas.height, prog);
                },
            };
            video.addEventListener('play', handlers.onPlay);
            video.addEventListener('pause', handlers.onPause);
            video.addEventListener('seeked', handlers.onSeeked);
            video.addEventListener('ended', handlers.onPause);

            state = {
                canvasEl: canvas,
                video: video,
                audioCtx: analyser ? analyser.ctx : null,
                analyser: analyser,
                peaks: peaks,
                handlers: handlers,
                raf: 0,
                resizeListener: sizeCanvas,
            };

            return { destroy: destroyState };
        },
        unbind: destroyState,
        isAudioOnly: isAudioOnly,
    };
})();
